import React, { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";

// A self-contained holographic HUD dashboard recreated in SVG to match the
// provided reference (neon frame, circular gauges, line/waveform + bar charts,
// progress bars, pill controls). Animated with Framer Motion, reduced-motion aware.

const Gauge = ({ cx, cy, r, pct, color, delay = 0, reduce }) => {
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - pct);
  return (
    <g>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="6" />
      <motion.circle
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth="6"
        strokeLinecap="round"
        strokeDasharray={circ}
        transform={`rotate(-90 ${cx} ${cy})`}
        style={{ filter: `drop-shadow(0 0 6px ${color})` }}
        initial={{ strokeDashoffset: circ }}
        animate={{ strokeDashoffset: reduce ? offset : offset }}
        transition={{ duration: reduce ? 0 : 1.4, delay, ease: [0.22, 1, 0.36, 1] }}
      />
    </g>
  );
};

export const HudDashboard = () => {
  const reduce = useReducedMotion();
  const [tick, setTick] = useState(0);

  // gentle live update of the waveform to feel alive (paused for reduced motion)
  useEffect(() => {
    if (reduce) return;
    const iv = setInterval(() => setTick((t) => t + 1), 2600);
    return () => clearInterval(iv);
  }, [reduce]);

  const wavePath = (seed) => {
    const pts = [];
    for (let i = 0; i <= 12; i++) {
      const x = 250 + (i / 12) * 250;
      const y = 150 + Math.sin(i * 0.9 + seed) * 18 + Math.cos(i * 0.5 + seed) * 8;
      pts.push(`${x},${y.toFixed(1)}`);
    }
    return `M ${pts.join(" L ")}`;
  };

  const bars = [
    { id: "b1", h: 46 },
    { id: "b2", h: 72 },
    { id: "b3", h: 38 },
    { id: "b4", h: 88 },
    { id: "b5", h: 60 },
    { id: "b6", h: 78 },
    { id: "b7", h: 52 },
  ];
  const progresses = [
    { label: "MODEL SYNC", pct: 0.86, color: "#a020f0" },
    { label: "THROUGHPUT", pct: 0.64, color: "#39ff14" },
    { label: "UPTIME", pct: 0.97, color: "#a020f0" },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
      animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className={`relative w-full ${reduce ? "" : "floaty"}`}
    >
      <div className="neon-frame relative rounded-2xl glass-strong p-3 shadow-[0_30px_80px_rgba(0,0,0,0.6)]">
        <svg viewBox="0 0 760 520" className="w-full h-auto" role="img" aria-label="Animated AI analytics dashboard">
          <defs>
            <pattern id="dots" width="18" height="18" patternUnits="userSpaceOnUse">
              <circle cx="1" cy="1" r="1" fill="rgba(255,255,255,0.06)" />
            </pattern>
            <linearGradient id="barGrad" x1="0" y1="1" x2="0" y2="0">
              <stop offset="0%" stopColor="#a020f0" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#39ff14" stopOpacity="0.9" />
            </linearGradient>
          </defs>

          <rect x="0" y="0" width="760" height="520" fill="url(#dots)" rx="16" />

          {/* Panel: gauges */}
          <g>
            <rect x="20" y="20" width="210" height="210" rx="14" fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.08)" />
            <text x="38" y="46" fill="#b8b8b8" fontSize="11" letterSpacing="2">SYSTEM LOAD</text>
            <Gauge cx={90} cy={135} r={44} pct={0.72} color="#39ff14" delay={0.2} reduce={reduce} />
            <Gauge cx={90} cy={135} r={30} pct={0.55} color="#a020f0" delay={0.35} reduce={reduce} />
            <text x="90" y="140" fill="#fff" fontSize="20" fontWeight="600" textAnchor="middle">72%</text>
            <Gauge cx={178} cy={110} r={26} pct={0.88} color="#a020f0" delay={0.5} reduce={reduce} />
            <text x="178" y="115" fill="#fff" fontSize="12" textAnchor="middle">88</text>
            <Gauge cx={178} cy={180} r={26} pct={0.4} color="#39ff14" delay={0.6} reduce={reduce} />
            <text x="178" y="185" fill="#fff" fontSize="12" textAnchor="middle">40</text>
          </g>

          {/* Panel: KPI cards */}
          <g>
            <rect x="246" y="20" width="110" height="100" rx="12" fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.08)" />
            <text x="262" y="46" fill="#b8b8b8" fontSize="10" letterSpacing="1.5">LATENCY</text>
            <text x="262" y="78" fill="#39ff14" fontSize="26" fontWeight="700">24ms</text>
            <text x="262" y="100" fill="#b8b8b8" fontSize="10">-12% vs last hr</text>

            <rect x="368" y="20" width="110" height="100" rx="12" fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.08)" />
            <text x="384" y="46" fill="#b8b8b8" fontSize="10" letterSpacing="1.5">REQUESTS</text>
            <text x="384" y="78" fill="#a020f0" fontSize="26" fontWeight="700">1.2M</text>
            <text x="384" y="100" fill="#b8b8b8" fontSize="10">+8% today</text>
          </g>

          {/* Panel: waveform / line chart */}
          <g>
            <rect x="246" y="132" width="232" height="98" rx="12" fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.08)" />
            <motion.path
              key={tick}
              d={wavePath(tick)}
              fill="none"
              stroke="#39ff14"
              strokeWidth="2.5"
              style={{ filter: "drop-shadow(0 0 5px #39ff14)" }}
              initial={{ pathLength: reduce ? 1 : 0, opacity: 0.6 }}
              animate={{ pathLength: 1, opacity: 1 }}
              transition={{ duration: reduce ? 0 : 1.6, ease: "easeInOut" }}
            />
            <motion.path
              d={wavePath(tick + 2)}
              fill="none"
              stroke="#a020f0"
              strokeWidth="2"
              strokeOpacity="0.7"
              style={{ filter: "drop-shadow(0 0 4px #a020f0)" }}
              initial={{ pathLength: reduce ? 1 : 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: reduce ? 0 : 1.8, ease: "easeInOut" }}
            />
          </g>

          {/* Panel: right analytics */}
          <g>
            <rect x="494" y="20" width="246" height="210" rx="14" fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.08)" />
            <text x="512" y="46" fill="#b8b8b8" fontSize="11" letterSpacing="2">NEURAL ACTIVITY</text>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <motion.line
                key={`nline-${72 + i * 26}`}
                x1={512}
                y1={72 + i * 26}
                x2={720}
                y2={72 + i * 26}
                stroke="rgba(255,255,255,0.06)"
                strokeWidth="1"
              />
            ))}
            <motion.path
              d="M 512,150 C 560,90 600,190 650,120 S 720,90 728,110"
              fill="none"
              stroke="#a020f0"
              strokeWidth="2.5"
              style={{ filter: "drop-shadow(0 0 5px #a020f0)" }}
              initial={{ pathLength: reduce ? 1 : 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: reduce ? 0 : 2, ease: "easeInOut", delay: 0.3 }}
            />
          </g>

          {/* Panel: bar chart */}
          <g>
            <rect x="20" y="246" width="330" height="180" rx="14" fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.08)" />
            <text x="38" y="272" fill="#b8b8b8" fontSize="11" letterSpacing="2">PIPELINE VOLUME</text>
            {bars.map((bar, i) => (
              <motion.rect
                key={bar.id}
                x={44 + i * 42}
                width="24"
                rx="5"
                fill="url(#barGrad)"
                initial={{ height: 0, y: 406 }}
                whileInView={{ height: bar.h + 20, y: 406 - (bar.h + 20) }}
                viewport={{ once: true }}
                transition={{ duration: reduce ? 0 : 0.8, delay: reduce ? 0 : 0.1 * i, ease: [0.22, 1, 0.36, 1] }}
              />
            ))}
          </g>

          {/* Panel: progress bars */}
          <g>
            <rect x="366" y="246" width="374" height="180" rx="14" fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.08)" />
            <text x="384" y="272" fill="#b8b8b8" fontSize="11" letterSpacing="2">DEPLOYMENTS</text>
            {progresses.map((p, i) => (
              <g key={p.label}>
                <text x="384" y={306 + i * 40} fill="#fff" fontSize="11">{p.label}</text>
                <rect x={384} y={314 + i * 40} width="320" height="8" rx="4" fill="rgba(255,255,255,0.08)" />
                <motion.rect
                  x={384}
                  y={314 + i * 40}
                  height="8"
                  rx="4"
                  fill={p.color}
                  style={{ filter: `drop-shadow(0 0 5px ${p.color})` }}
                  initial={{ width: 0 }}
                  whileInView={{ width: 320 * p.pct }}
                  viewport={{ once: true }}
                  transition={{ duration: reduce ? 0 : 1.2, delay: reduce ? 0 : 0.15 * i, ease: [0.22, 1, 0.36, 1] }}
                />
              </g>
            ))}
          </g>

          {/* Bottom pill controls */}
          <g>
            {["CORE", "AGENTS", "DATA", "CLOUD"].map((t, i) => (
              <g key={t}>
                <rect x={20 + i * 92} y="448" width="82" height="34" rx="17" fill="rgba(255,255,255,0.03)" stroke={i % 2 ? "rgba(160,32,240,0.35)" : "rgba(57,255,20,0.35)"} />
                <text x={61 + i * 92} y="470" fill="#fff" fontSize="11" textAnchor="middle" letterSpacing="1">{t}</text>
              </g>
            ))}
            <circle cx="700" cy="465" r="16" fill="rgba(57,255,20,0.12)" stroke="rgba(57,255,20,0.5)" />
            <circle cx="700" cy="465" r="5" fill="#39ff14" style={{ filter: "drop-shadow(0 0 6px #39ff14)" }} />
          </g>
        </svg>
      </div>
    </motion.div>
  );
};
